/**
 * User preferences
 */

import { EDITOR_MODE_HTML, POST_TYPE_LINK } from '@/lib/constants'

const EDITOR_MODE_STORAGE_KEY = 'editorMode'
const POST_TYPE_STORAGE_KEY = 'postType'
const SHOW_SURVEY_STORAGE_KEY = 'postpone:showFeedbackSurveyV2'
const SHOW_SOCIAL_MEDIA_SURVEY_STORAGE_KEY = 'showSocialMediaSurvey'
const SHOW_ANNUAL_UPSELL_BANNER_STORAGE_KEY = 'showAnnualUpsellBanner'
const SHOW_UPGRADE_BANNER_STORAGE_KEY = 'postpone:sub'
const SHOW_SUBREDDIT_SETTINGS_STORAGE_KEY = 'postpone:sst'
const DASHBOARD_DISPLAY_STORAGE_KEY = 'postpone:dashboardDisplay'
const PREVIEW_IMAGES_KEY = 'previewImages'
const DEFAULT_PER_PAGE_STORAGE_KEY = 'defaultPerPage'
const LAST_READ_CHANGELOG_STORAGE_KEY = 'postpone:lastReadChangelog'
const LAST_USED_INBOX_USERNAME_STORAGE_KEY = 'postpone:lastUsedInboxUsername'
const LAST_USED_CALENDAR_VIEW_STORAGE_KEY = 'postpone:lastUsedCalendarView'

class FakeLocalStorage {
  getItem(key) {
    return ''
  }

  setItem(key, value) {
    return ''
  }
}

const storage = process.browser ? localStorage : new FakeLocalStorage()

export class UserPrefs {
  constructor(params) {
    this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    this._editorMode = null
    this._postType = null
    this._showAnnualUpsellBanner = null
    this._showUpgradeBanner = null
    this._showSubredditSettingsTooltip = null
    this._previewImages = null
    this._defaultPerPage = null
    this._dashboardDisplay = null
    this._lastReadChangelog = null
    this._lastUsedInboxUsername = null
    this._lastUsedCalendarView = null

    this._showSurvey = null
    this._showSocialMediaSurvey = null
  }

  get editorMode() {
    this._editorMode =
      storage.getItem(EDITOR_MODE_STORAGE_KEY) || EDITOR_MODE_HTML
    return this._editorMode
  }

  set editorMode(editorMode) {
    storage.setItem(EDITOR_MODE_STORAGE_KEY, editorMode)
    this._editorMode = editorMode
  }

  get dashboardDisplay() {
    this._dashboardDisplay =
      storage.getItem(DASHBOARD_DISPLAY_STORAGE_KEY) || 'list'
    return this._dashboardDisplay
  }

  set dashboardDisplay(dashboardDisplay) {
    storage.setItem(DASHBOARD_DISPLAY_STORAGE_KEY, dashboardDisplay)
    this._dashboardDisplay = dashboardDisplay
  }

  get postType() {
    this._postType = storage.getItem(POST_TYPE_STORAGE_KEY) || POST_TYPE_LINK
    return this._postType
  }

  set postType(postType) {
    storage.setItem(POST_TYPE_STORAGE_KEY, postType)
    this._postType = postType
  }

  get showSurvey() {
    this._showSurvey = storage.getItem(SHOW_SURVEY_STORAGE_KEY) || '1'
    return this._showSurvey === '1'
  }

  set showSurvey(showSurvey) {
    const showSurveyString = showSurvey ? '1' : '0'
    storage.setItem(SHOW_SURVEY_STORAGE_KEY, showSurveyString)
    this._showSurvey = showSurveyString
  }

  get showSocialMediaSurvey() {
    this._showSocialMediaSurvey =
      storage.getItem(SHOW_SOCIAL_MEDIA_SURVEY_STORAGE_KEY) || '1'
    return this._showSocialMediaSurvey === '1'
  }

  set showSocialMediaSurvey(showSocialMediaSurvey) {
    const showSocialMediaSurveyString = showSocialMediaSurvey ? '1' : '0'
    storage.setItem(
      SHOW_SOCIAL_MEDIA_SURVEY_STORAGE_KEY,
      showSocialMediaSurveyString
    )
    this._showSocialMediaSurvey = showSocialMediaSurveyString
  }

  get showAnnualUpsellBanner() {
    this._showAnnualUpsellBanner =
      storage.getItem(SHOW_ANNUAL_UPSELL_BANNER_STORAGE_KEY) || '1'
    return this._showAnnualUpsellBanner === '1'
  }

  set showAnnualUpsellBanner(showAnnualUpsellBanner) {
    const showAnnualUpsellBannerString = showAnnualUpsellBanner ? '1' : '0'
    storage.setItem(
      SHOW_ANNUAL_UPSELL_BANNER_STORAGE_KEY,
      showAnnualUpsellBannerString
    )
    this._showAnnualUpsellBanner = showAnnualUpsellBannerString
  }

  get showUpgradeBanner() {
    this._showUpgradeBanner =
      storage.getItem(SHOW_UPGRADE_BANNER_STORAGE_KEY) || '1'
    return this._showUpgradeBanner === '1'
  }

  set showUpgradeBanner(showUpgradeBanner) {
    const showUpgradeBannerString = showUpgradeBanner ? '1' : '0'
    storage.setItem(SHOW_UPGRADE_BANNER_STORAGE_KEY, showUpgradeBannerString)
    this._showUpgradeBanner = showUpgradeBannerString
  }

  get showSubredditSettingsTooltip() {
    this._showSubredditSettingsTooltip =
      storage.getItem(SHOW_SUBREDDIT_SETTINGS_STORAGE_KEY) || '1'
    return this._showSubredditSettingsTooltip === '1'
  }

  set showSubredditSettingsTooltip(showSubredditSettingsTooltip) {
    const showSubredditSettingsTooltipString = showSubredditSettingsTooltip
      ? '1'
      : '0'
    storage.setItem(
      SHOW_SUBREDDIT_SETTINGS_STORAGE_KEY,
      showSubredditSettingsTooltipString
    )
    this._showSubredditSettingsTooltip = showSubredditSettingsTooltipString
  }

  get previewImages() {
    this._previewImages = storage.getItem(PREVIEW_IMAGES_KEY) || '1'
    return this._previewImages === '1'
  }

  set previewImages(previewImages) {
    const previewImagesString = previewImages ? '1' : '0'
    storage.setItem(PREVIEW_IMAGES_KEY, previewImagesString)
    this._previewImages = previewImagesString
  }

  get defaultPerPage() {
    this._defaultPerPage = storage.getItem(DEFAULT_PER_PAGE_STORAGE_KEY) || '10'
    return parseInt(this._defaultPerPage)
  }

  set defaultPerPage(defaultPerPage) {
    const defaultPerPageString = defaultPerPage.toString()
    storage.setItem(DEFAULT_PER_PAGE_STORAGE_KEY, defaultPerPageString)
    this._defaultPerPage = defaultPerPageString
  }

  get lastUsedInboxUsername() {
    this._lastUsedInboxUsername =
      storage.getItem(LAST_USED_INBOX_USERNAME_STORAGE_KEY) || ''
    return this._lastUsedInboxUsername
  }

  set lastUsedInboxUsername(lastUsedInboxUsername) {
    storage.setItem(LAST_USED_INBOX_USERNAME_STORAGE_KEY, lastUsedInboxUsername)
    this._lastUsedInboxUsername = lastUsedInboxUsername
  }

  get lastUsedCalendarView() {
    this._lastUsedCalendarView =
      storage.getItem(LAST_USED_CALENDAR_VIEW_STORAGE_KEY) || ''
    return this._lastUsedCalendarView
  }

  set lastUsedCalendarView(lastUsedCalendarView) {
    storage.setItem(LAST_USED_CALENDAR_VIEW_STORAGE_KEY, lastUsedCalendarView)
    this._lastUsedCalendarView = lastUsedCalendarView
  }

  get lastReadChangelog() {
    let lastReadChangelogString =
      storage.getItem(LAST_READ_CHANGELOG_STORAGE_KEY) || null
    this._lastReadChangelog = lastReadChangelogString
      ? new Date(lastReadChangelogString)
      : null
    return this._lastReadChangelog
  }

  trackReadChangelog() {
    const date = new Date().toISOString().split('T')[0]
    storage.setItem(LAST_READ_CHANGELOG_STORAGE_KEY, date)
    this._lastReadChangelog = date
  }
}
